/**
 * Flow Ribbons Component
 * Displays a 3-column Sankey diagram showing:
 * Pipeline → Deployment Stage → Top Engineering Friction
 * 
 * Users can click on stages or ribbons to drill into customer details.
 * Supports multiple visualization modes: Sankey, Funnel, Matrix Grid
 */

import React, { useMemo, useState, useCallback } from "react";
import { Box, Typography, Paper, Skeleton, alpha, Tooltip, InputBase, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { Search16Regular, Flow20Regular, LineHorizontal320Regular, Organization20Regular } from "@fluentui/react-icons";
import {
    IFlowRibbonsProps,
    ISankeyNode,
    ISankeyLink,
    ICustomerChip,
    IFrictionLegendItem,
    LivePreviewTimeRange
} from "./CustomerPerception.interfaces";
import { THEME, SANKEY_COLUMNS, FRICTION_COLORS, PIPELINE_COLORS, DEPLOYMENT_COLORS, TIME_RANGE_OPTIONS } from "./CustomerPerception.constants";
import { FLOW_RIBBONS_STYLES, CUSTOMER_PERCEPTION_STYLES, JOURNEY_BOARD_STYLES } from "./CustomerPerception.styles";

type ViewMode = "sankey" | "alluvial" | "arc";

interface SankeyModel {
    nodes: Array<{
        id: string;
        column: string;
        label: string;
        customers: number;
        color: string;
        x: number;
        y: number;
        height: number;
    }>;
    links: Array<{
        key: string;
        sourceId: string;
        targetId: string;
        value: number;
        sourceColor: string;
        targetColor: string;
        path: string;
    }>;
    viewBox: { width: number; height: number };
}

/**
 * Builds Sankey diagram geometry from nodes and links data
 * Creates a clean, readable flow visualization with proper spacing
 */
const buildSankeyModel = (
    nodes: ISankeyNode[],
    links: ISankeyLink[]
): SankeyModel => {
    const NODE_BAR_WIDTH = 5;
    const COLUMN_SPACING = 260; // Space between columns for ribbon flow
    const NODE_VERTICAL_GAP = 2; // Gap between nodes in same column
    const NODE_MIN_HEIGHT = 16;
    const NODE_MAX_HEIGHT = 28;
    const LEFT_MARGIN = 5;
    const TOP_MARGIN = 22;
    const RIGHT_MARGIN = 150; // Space for right column labels (friction names are long)

    // Group nodes by column in display order
    const columnOrder = [
        SANKEY_COLUMNS.PREVIEW_PIPELINE,
        SANKEY_COLUMNS.DEPLOYMENT_STAGE,
        SANKEY_COLUMNS.TOP_ENGINEERING_FRICTION
    ];

    const nodesByColumn: Record<string, ISankeyNode[]> = {};
    columnOrder.forEach(col => { nodesByColumn[col] = []; });

    nodes.forEach(node => {
        if (nodesByColumn[node.column]) {
            nodesByColumn[node.column].push(node);
        }
    });

    // Sort nodes within each column by customer count (descending) for better flow
    columnOrder.forEach(col => {
        nodesByColumn[col].sort((a, b) => b.customers - a.customers);
    });

    // Calculate total customers for proportional height scaling
    const totalCustomersPerColumn: Record<string, number> = {};
    columnOrder.forEach(col => {
        totalCustomersPerColumn[col] = nodesByColumn[col].reduce((sum, n) => sum + n.customers, 0) || 1;
    });

    // Calculate dynamic height based on the column with most nodes
    const maxNodesInColumn = Math.max(...columnOrder.map(col => nodesByColumn[col].length));
    const calculatedHeight = TOP_MARGIN + 10 + maxNodesInColumn * (NODE_MIN_HEIGHT + NODE_VERTICAL_GAP);
    const VIEW_HEIGHT = Math.max(300, calculatedHeight);

    // Position nodes with proportional heights
    const positionedNodes: SankeyModel["nodes"] = [];
    const usableHeight = VIEW_HEIGHT - TOP_MARGIN * 2;

    columnOrder.forEach((col, colIdx) => {
        const colNodes = nodesByColumn[col];
        const totalGaps = (colNodes.length - 1) * NODE_VERTICAL_GAP;
        const availableHeight = usableHeight - totalGaps;

        let currentY = TOP_MARGIN;
        colNodes.forEach(node => {
            // Proportional height based on customer count
            const proportion = node.customers / totalCustomersPerColumn[col];
            const rawHeight = proportion * availableHeight;
            const height = Math.max(NODE_MIN_HEIGHT, Math.min(NODE_MAX_HEIGHT, rawHeight));

            positionedNodes.push({
                id: `${col}:${node.node}`,
                column: col,
                label: node.node,
                customers: node.customers,
                color: node.color || THEME.primary,
                x: LEFT_MARGIN + colIdx * COLUMN_SPACING,
                y: currentY,
                height
            });
            currentY += height + NODE_VERTICAL_GAP;
        });
    });

    // Create node lookup by label
    const nodeMap = new Map(positionedNodes.map(n => [n.label, n]));

    // Track vertical offset for each node to stack ribbons properly
    const nodeSourceOffset: Record<string, number> = {};
    const nodeTargetOffset: Record<string, number> = {};
    positionedNodes.forEach(n => {
        nodeSourceOffset[n.id] = 0;
        nodeTargetOffset[n.id] = 0;
    });

    // Sort links by value (descending) for better visual layering
    const sortedLinks = [...links].sort((a, b) => b.value - a.value);

    // Build links with smooth bezier curves
    const positionedLinks: SankeyModel["links"] = [];
    sortedLinks.forEach((link, idx) => {
        const sourceNode = nodeMap.get(link.source);
        const targetNode = nodeMap.get(link.target);

        if (sourceNode && targetNode) {
            // Calculate ribbon thickness based on flow value
            const maxLinkValue = Math.max(...links.map(l => l.value), 1);
            const thickness = Math.max(1.5, Math.min(8, (link.value / maxLinkValue) * 6 + 1.5));

            // Calculate ribbon positions with stacking
            const sourceX = sourceNode.x + NODE_BAR_WIDTH;
            const sourceYOffset = nodeSourceOffset[sourceNode.id];
            const sourceY = sourceNode.y + sourceYOffset + thickness / 2;

            const targetX = targetNode.x;
            const targetYOffset = nodeTargetOffset[targetNode.id];
            const targetY = targetNode.y + targetYOffset + thickness / 2;

            // Update offsets for next ribbon
            nodeSourceOffset[sourceNode.id] += thickness + 1;
            nodeTargetOffset[targetNode.id] += thickness + 1;

            // Create smooth S-curve bezier path
            const midX = (sourceX + targetX) / 2;

            const path = `
                M ${sourceX} ${sourceY - thickness / 2}
                C ${midX} ${sourceY - thickness / 2}, ${midX} ${targetY - thickness / 2}, ${targetX} ${targetY - thickness / 2}
                L ${targetX} ${targetY + thickness / 2}
                C ${midX} ${targetY + thickness / 2}, ${midX} ${sourceY + thickness / 2}, ${sourceX} ${sourceY + thickness / 2}
                Z
            `;

            positionedLinks.push({
                key: `link-${idx}`,
                sourceId: sourceNode.id,
                targetId: targetNode.id,
                value: link.value,
                sourceColor: sourceNode.color,
                targetColor: targetNode.color,
                path
            });
        }
    });

    return {
        nodes: positionedNodes,
        links: positionedLinks,
        viewBox: {
            width: LEFT_MARGIN + 2 * COLUMN_SPACING + RIGHT_MARGIN,
            height: VIEW_HEIGHT
        }
    };
};

/**
 * Flow Ribbons Section Component
 */
export const FlowRibbonsSection: React.FC<IFlowRibbonsProps> = ({
    sankeyNodes,
    sankeyLinks,
    frictionLegend,
    customerChips,
    cohortCustomers,
    isLoading,
    onStageClick,
    selectedStage,
    livePreviewUsage
}) => {
    const [hoveredNode, setHoveredNode] = useState<string | null>(null);
    const [hoveredLink, setHoveredLink] = useState<string | null>(null);
    const [customerSearch, setCustomerSearch] = useState("");
    const [viewMode, setViewMode] = useState<ViewMode>("sankey");
    const [timeRange, setTimeRange] = useState<LivePreviewTimeRange>("Last 30 days");

    const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
        if (newMode) setViewMode(newMode);
    };

    const handleTimeRangeChange = useCallback((_: React.MouseEvent<HTMLElement>, newValue: LivePreviewTimeRange | null) => {
        if (newValue) {
            setTimeRange(newValue);
        }
    }, []);

    // Build Sankey model
    const model = useMemo(() => {
        if (sankeyNodes.length === 0) return null;
        return buildSankeyModel(sankeyNodes, sankeyLinks);
    }, [sankeyNodes, sankeyLinks]);

    // Build Paths data - shows complete flow paths (Pipeline → Deployment → Friction)
    const pathsData = useMemo(() => {
        // Get links from Pipeline to Deployment
        const pipelineToDeployment = sankeyLinks.filter(l => {
            const sourceNode = sankeyNodes.find(n => n.node === l.source);
            const targetNode = sankeyNodes.find(n => n.node === l.target);
            return sourceNode?.column === SANKEY_COLUMNS.PREVIEW_PIPELINE &&
                targetNode?.column === SANKEY_COLUMNS.DEPLOYMENT_STAGE;
        });

        // Get links from Deployment to Friction
        const deploymentToFriction = sankeyLinks.filter(l => {
            const sourceNode = sankeyNodes.find(n => n.node === l.source);
            const targetNode = sankeyNodes.find(n => n.node === l.target);
            return sourceNode?.column === SANKEY_COLUMNS.DEPLOYMENT_STAGE &&
                targetNode?.column === SANKEY_COLUMNS.TOP_ENGINEERING_FRICTION;
        });

        // Build complete paths
        const paths: Array<{
            pipeline: string;
            deployment: string;
            friction: string;
            customers: number;
            pipelineColor: string;
            deploymentColor: string;
            frictionColor: string;
        }> = [];

        pipelineToDeployment.forEach(pd => {
            deploymentToFriction
                .filter(df => df.source === pd.target)
                .forEach(df => {
                    // Use minimum value as the path flow (bottleneck)
                    const pathValue = Math.min(pd.value, df.value);
                    if (pathValue > 0) {
                        paths.push({
                            pipeline: pd.source,
                            deployment: pd.target,
                            friction: df.target,
                            customers: pathValue,
                            pipelineColor: PIPELINE_COLORS[pd.source] || "#3B82F6",
                            deploymentColor: DEPLOYMENT_COLORS[pd.target] || "#10B981",
                            frictionColor: FRICTION_COLORS[df.target] || "#9CA3AF"
                        });
                    }
                });
        });

        // Sort by customer count descending
        return paths.sort((a, b) => b.customers - a.customers);
    }, [sankeyNodes, sankeyLinks]);

    // Build summary cards data
    const cardsData = useMemo(() => {
        const pipelineNodes = sankeyNodes.filter(n => n.column === SANKEY_COLUMNS.PREVIEW_PIPELINE);
        const deploymentNodes = sankeyNodes.filter(n => n.column === SANKEY_COLUMNS.DEPLOYMENT_STAGE);
        const frictionNodes = sankeyNodes.filter(n => n.column === SANKEY_COLUMNS.TOP_ENGINEERING_FRICTION);

        const totalCustomers = pipelineNodes.reduce((sum, n) => sum + n.customers, 0);
        const topPipeline = pipelineNodes.sort((a, b) => b.customers - a.customers)[0];
        const topDeployment = deploymentNodes.sort((a, b) => b.customers - a.customers)[0];
        const topFriction = frictionNodes.sort((a, b) => b.customers - a.customers)[0];

        // Top 5 paths
        const topPaths = pathsData.slice(0, 5);

        return {
            totalCustomers,
            topPipeline,
            topDeployment,
            topFriction,
            topPaths,
            pipelineBreakdown: pipelineNodes.sort((a, b) => b.customers - a.customers),
            deploymentBreakdown: deploymentNodes.sort((a, b) => b.customers - a.customers),
            frictionBreakdown: frictionNodes.sort((a, b) => b.customers - a.customers)
        };
    }, [sankeyNodes, pathsData]);

    // Get the appropriate customer list based on selection
    // When a stage is selected, use cohortCustomers (filtered by stage)
    // Otherwise, use all customerChips
    const displayCustomers = useMemo(() => {
        return selectedStage && cohortCustomers ? cohortCustomers : customerChips;
    }, [selectedStage, cohortCustomers, customerChips]);

    // Filter chips by search
    const filteredChips = useMemo(() => {
        if (!customerSearch) return displayCustomers;
        const search = customerSearch.toLowerCase();
        return displayCustomers.filter(c =>
            c.customer.toLowerCase().includes(search) ||
            c.pm.toLowerCase().includes(search)
        );
    }, [displayCustomers, customerSearch]);

    const handleNodeClick = useCallback((label: string) => {
        onStageClick?.(label);
    }, [onStageClick]);

    if (isLoading) {
        return (
            <Box>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: "12px", mb: 2 }} />
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: "12px" }} />
            </Box>
        );
    }

    if (!model) {
        return (
            <Typography sx={{ textAlign: "center", color: THEME.textMuted, py: 4 }}>
                No data available for Flow Ribbons visualization.
            </Typography>
        );
    }

    // Constants for SVG rendering - must match buildSankeyModel values
    const NODE_BAR_WIDTH = 5;
    const COLUMN_SPACING = 260;
    const LEFT_MARGIN = 5;

    // View mode descriptions
    const viewDescriptions: Record<ViewMode, { title: string; desc: string; howTo: React.ReactNode }> = {
        sankey: {
            title: "Flow Ribbons",
            desc: "Every ribbon is a group of customers. Width = number of customers.",
            howTo: <>Left = <b style={{ color: "#0078D4" }}>preview pipeline</b>. Middle = <b style={{ color: "#0078D4" }}>deployment</b>. Right = <b style={{ color: "#0078D4" }}>engineering blocker</b>. Thick ribbons into <b style={{ color: "#EF4444" }}>red/orange</b> = leadership talking points.</>
        },
        alluvial: {
            title: "Alluvial Flow",
            desc: "Straight diagonal lines show customer flow between stages.",
            howTo: <>Line thickness = <b style={{ color: "#0078D4" }}>customer count</b>. Follow any line left-to-right to trace the complete journey. Thicker lines = more customers.</>
        },
        arc: {
            title: "Arc Diagram",
            desc: "Arcs connect stages showing the flow relationships.",
            howTo: <>Arc height = <b style={{ color: "#0078D4" }}>customer volume</b>. Taller arcs = more customers. Colors show the <b style={{ color: "#0078D4" }}>source stage</b>.</>
        }
    };

    const currentView = viewDescriptions[viewMode];

    return (
        <Box>
            {/* Title and Description */}
            <Paper elevation={0} sx={{ ...(CUSTOMER_PERCEPTION_STYLES.contentCard as object), mb: 1.5, p: 1.5 }}>
                {/* Header with View Toggle */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A2E", mb: 0.25 }}>
                            {currentView.title}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: "#64748B" }}>
                            {currentView.desc}
                        </Typography>
                    </Box>

                    {/* View Mode Toggle */}
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewModeChange}
                        size="small"
                        sx={{
                            backgroundColor: "#F1F5F9",
                            borderRadius: "8px",
                            p: 0.25,
                            "& .MuiToggleButton-root": {
                                border: "none",
                                borderRadius: "6px !important",
                                px: 1.5,
                                py: 0.5,
                                fontSize: "11px",
                                fontWeight: 500,
                                color: "#64748B",
                                textTransform: "none",
                                gap: 0.5,
                                "&.Mui-selected": {
                                    backgroundColor: "#FFFFFF",
                                    color: "#0078D4",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                    "&:hover": { backgroundColor: "#FFFFFF" }
                                },
                                "&:hover": { backgroundColor: alpha("#0078D4", 0.08) }
                            }
                        }}
                    >
                        <ToggleButton value="sankey">
                            <Flow20Regular style={{ fontSize: 14 }} />
                            Flow
                        </ToggleButton>
                        <ToggleButton value="alluvial">
                            <LineHorizontal320Regular style={{ fontSize: 14 }} />
                            Lines
                        </ToggleButton>
                        <ToggleButton value="arc">
                            <Organization20Regular style={{ fontSize: 14 }} />
                            Arcs
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {/* How to Read */}
                <Box sx={{ ...CUSTOMER_PERCEPTION_STYLES.howToReadBox as object, py: 0.5, px: 1, mb: 0.75 }}>
                    <Typography component="span" sx={{ fontSize: "12px", fontWeight: 600, color: "#64748B", mr: 0.5 }}>
                        How to read it:
                    </Typography>
                    <Typography component="span" sx={{ fontSize: "12px", color: "#1A1A2E" }}>
                        {currentView.howTo}
                    </Typography>
                </Box>

                {/* Visualization Area */}
                {viewMode === "sankey" && model && (
                    <Box sx={{
                        width: "100%",
                        backgroundColor: "#FAFBFC",
                        borderRadius: "6px",
                        border: "1px solid #E2E8F0",
                        mt: 0.75,
                        p: 0.75
                    }}>
                        <Box
                            component="svg"
                            viewBox={`0 0 ${model.viewBox.width} ${model.viewBox.height}`}
                            preserveAspectRatio="xMidYMid meet"
                            sx={{ width: "100%", height: "auto", display: "block" }}
                        >
                            <defs>
                                {model.links.map((link, i) => (
                                    <linearGradient key={`grad-${i}`} id={`cp-ribbon-${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor={link.sourceColor} stopOpacity={0.55} />
                                        <stop offset="100%" stopColor={link.targetColor} stopOpacity={0.55} />
                                    </linearGradient>
                                ))}
                            </defs>

                            {/* Column Headers */}
                            {[SANKEY_COLUMNS.PREVIEW_PIPELINE, SANKEY_COLUMNS.DEPLOYMENT_STAGE, SANKEY_COLUMNS.TOP_ENGINEERING_FRICTION].map((col, idx) => (
                                <text
                                    key={col}
                                    x={LEFT_MARGIN + idx * COLUMN_SPACING + NODE_BAR_WIDTH + 3}
                                    y={16}
                                    textAnchor="start"
                                    style={{
                                        fontSize: 5,
                                        fontWeight: 700,
                                        fill: "#64748B",
                                        letterSpacing: "0.3px",
                                        textTransform: "uppercase"
                                    }}
                                >
                                    {col}
                                </text>
                            ))}

                            {/* Links (ribbons) - render first so nodes appear on top */}
                            {model.links.map((link, i) => (
                                <Tooltip
                                    key={link.key}
                                    title={`${link.value} customers: ${link.sourceId.split(":")[1]} → ${link.targetId.split(":")[1]}`}
                                    arrow
                                >
                                    <path
                                        d={link.path}
                                        fill={`url(#cp-ribbon-${i})`}
                                        style={{
                                            opacity: hoveredLink === link.key ? 0.95 : (hoveredLink ? 0.25 : 0.7),
                                            cursor: "pointer",
                                            transition: "opacity 0.15s ease"
                                        }}
                                        onMouseEnter={() => setHoveredLink(link.key)}
                                        onMouseLeave={() => setHoveredLink(null)}
                                    />
                                </Tooltip>
                            ))}

                            {/* Nodes */}
                            {model.nodes.map(node => {
                                const isSelected = selectedStage === node.label;
                                const isHovered = hoveredNode === node.id;

                                return (
                                    <g key={node.id}>
                                        <Tooltip title={`${node.label}: ${node.customers} customers`} arrow>
                                            <rect
                                                x={node.x}
                                                y={node.y}
                                                width={NODE_BAR_WIDTH}
                                                height={node.height}
                                                rx={NODE_BAR_WIDTH / 2}
                                                fill={node.color}
                                                style={{
                                                    cursor: "pointer",
                                                    stroke: isSelected ? "#1A1A2E" : "transparent",
                                                    strokeWidth: isSelected ? 2 : 0,
                                                    opacity: isHovered ? 1 : 0.95,
                                                    transition: "all 0.15s ease"
                                                }}
                                                onMouseEnter={() => setHoveredNode(node.id)}
                                                onMouseLeave={() => setHoveredNode(null)}
                                                onClick={() => handleNodeClick(node.label)}
                                            />
                                        </Tooltip>
                                        {/* Node Label */}
                                        <text
                                            x={node.x + NODE_BAR_WIDTH + 3}
                                            y={node.y + node.height / 2 - 1}
                                            style={{
                                                fontSize: 5,
                                                fontWeight: 500,
                                                fill: "#1A1A2E",
                                                pointerEvents: "none"
                                            }}
                                        >
                                            {node.label}
                                        </text>
                                        {/* Customer Count */}
                                        <text
                                            x={node.x + NODE_BAR_WIDTH + 3}
                                            y={node.y + node.height / 2 + 6}
                                            style={{
                                                fontSize: 4,
                                                fill: "#0078D4",
                                                fontWeight: 600,
                                                pointerEvents: "none"
                                            }}
                                        >
                                            {node.customers} cust
                                        </text>
                                    </g>
                                );
                            })}
                        </Box>
                    </Box>
                )}

                {/* Alluvial View - Straight diagonal lines showing flow */}
                {viewMode === "alluvial" && model && (
                    <Box sx={{ width: "100%", backgroundColor: "#FAFBFC", borderRadius: "8px", border: "1px solid #E2E8F0", mt: 0.75, p: 1 }}>
                        <Box
                            component="svg"
                            viewBox={`0 0 ${model.viewBox.width} ${model.viewBox.height}`}
                            preserveAspectRatio="xMidYMid meet"
                            sx={{ width: "100%", height: "auto", display: "block" }}
                        >
                            {/* Column Headers */}
                            {[SANKEY_COLUMNS.PREVIEW_PIPELINE, SANKEY_COLUMNS.DEPLOYMENT_STAGE, SANKEY_COLUMNS.TOP_ENGINEERING_FRICTION].map((col, idx) => (
                                <text
                                    key={col}
                                    x={LEFT_MARGIN + idx * COLUMN_SPACING + NODE_BAR_WIDTH + 3}
                                    y={16}
                                    textAnchor="start"
                                    style={{ fontSize: 5, fontWeight: 700, fill: "#64748B", letterSpacing: "0.3px", textTransform: "uppercase" as const }}
                                >
                                    {col}
                                </text>
                            ))}

                            {/* Straight diagonal lines (Alluvial style) */}
                            {model.links.map((link, i) => {
                                const sourceNode = model.nodes.find(n => n.id === link.sourceId);
                                const targetNode = model.nodes.find(n => n.id === link.targetId);
                                if (!sourceNode || !targetNode) return null;

                                const sourceX = sourceNode.x + NODE_BAR_WIDTH;
                                const sourceY = sourceNode.y + sourceNode.height / 2;
                                const targetX = targetNode.x;
                                const targetY = targetNode.y + targetNode.height / 2;
                                const strokeWidth = Math.max(1, Math.min(link.value * 0.8, 8));

                                return (
                                    <Tooltip key={link.key} title={`${link.value} customers`} arrow>
                                        <line
                                            x1={sourceX}
                                            y1={sourceY}
                                            x2={targetX}
                                            y2={targetY}
                                            stroke={link.sourceColor}
                                            strokeWidth={strokeWidth}
                                            strokeOpacity={hoveredLink === link.key ? 0.9 : 0.4}
                                            style={{ cursor: "pointer", transition: "all 0.15s ease" }}
                                            onMouseEnter={() => setHoveredLink(link.key)}
                                            onMouseLeave={() => setHoveredLink(null)}
                                        />
                                    </Tooltip>
                                );
                            })}

                            {/* Nodes as circles */}
                            {model.nodes.map(node => (
                                <g key={node.id}>
                                    <Tooltip title={`${node.label}: ${node.customers} customers`} arrow>
                                        <circle
                                            cx={node.x + NODE_BAR_WIDTH / 2}
                                            cy={node.y + node.height / 2}
                                            r={Math.max(6, Math.min(node.customers * 0.5, 14))}
                                            fill={node.color}
                                            style={{ cursor: "pointer", transition: "all 0.15s ease" }}
                                            onClick={() => handleNodeClick(node.label)}
                                        />
                                    </Tooltip>
                                    <text
                                        x={node.x + NODE_BAR_WIDTH + 8}
                                        y={node.y + node.height / 2 + 2}
                                        style={{ fontSize: 5, fontWeight: 500, fill: "#1A1A2E", pointerEvents: "none" }}
                                    >
                                        {node.label} ({node.customers})
                                    </text>
                                </g>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Arc View - Curved arcs showing flow connections */}
                {viewMode === "arc" && model && (
                    <Box sx={{ width: "100%", backgroundColor: "#FAFBFC", borderRadius: "8px", border: "1px solid #E2E8F0", mt: 0.75, p: 1 }}>
                        <Box
                            component="svg"
                            viewBox={`0 0 ${model.viewBox.width} ${model.viewBox.height + 20}`}
                            preserveAspectRatio="xMidYMid meet"
                            sx={{ width: "100%", height: "auto", display: "block" }}
                        >
                            {/* Column Headers */}
                            {[SANKEY_COLUMNS.PREVIEW_PIPELINE, SANKEY_COLUMNS.DEPLOYMENT_STAGE, SANKEY_COLUMNS.TOP_ENGINEERING_FRICTION].map((col, idx) => (
                                <text
                                    key={col}
                                    x={LEFT_MARGIN + idx * COLUMN_SPACING + NODE_BAR_WIDTH + 3}
                                    y={16}
                                    textAnchor="start"
                                    style={{ fontSize: 5, fontWeight: 700, fill: "#64748B", letterSpacing: "0.3px", textTransform: "uppercase" as const }}
                                >
                                    {col}
                                </text>
                            ))}

                            {/* Arc connections with quadratic bezier curves */}
                            {model.links.map((link, i) => {
                                const sourceNode = model.nodes.find(n => n.id === link.sourceId);
                                const targetNode = model.nodes.find(n => n.id === link.targetId);
                                if (!sourceNode || !targetNode) return null;

                                const sourceX = sourceNode.x + NODE_BAR_WIDTH;
                                const sourceY = sourceNode.y + sourceNode.height / 2;
                                const targetX = targetNode.x;
                                const targetY = targetNode.y + targetNode.height / 2;

                                // Control point for quadratic bezier - creates an arc
                                const midX = (sourceX + targetX) / 2;
                                const arcHeight = Math.abs(targetY - sourceY) * 0.3 + 15;
                                const controlY = Math.min(sourceY, targetY) - arcHeight;

                                const strokeWidth = Math.max(1.5, Math.min(link.value * 0.6, 6));

                                return (
                                    <Tooltip key={link.key} title={`${link.value} customers: ${link.sourceId.split(":")[1]} → ${link.targetId.split(":")[1]}`} arrow>
                                        <path
                                            d={`M ${sourceX} ${sourceY} Q ${midX} ${controlY} ${targetX} ${targetY}`}
                                            fill="none"
                                            stroke={link.sourceColor}
                                            strokeWidth={strokeWidth}
                                            strokeOpacity={hoveredLink === link.key ? 0.85 : 0.35}
                                            strokeLinecap="round"
                                            style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                                            onMouseEnter={() => setHoveredLink(link.key)}
                                            onMouseLeave={() => setHoveredLink(null)}
                                        />
                                    </Tooltip>
                                );
                            })}

                            {/* Nodes as rounded rectangles */}
                            {model.nodes.map(node => (
                                <g key={node.id}>
                                    <Tooltip title={`${node.label}: ${node.customers} customers`} arrow>
                                        <rect
                                            x={node.x}
                                            y={node.y}
                                            width={NODE_BAR_WIDTH + 4}
                                            height={node.height}
                                            rx={3}
                                            fill={node.color}
                                            style={{ cursor: "pointer", transition: "all 0.15s ease" }}
                                            onClick={() => handleNodeClick(node.label)}
                                        />
                                    </Tooltip>
                                    <text
                                        x={node.x + NODE_BAR_WIDTH + 10}
                                        y={node.y + node.height / 2 + 2}
                                        style={{ fontSize: 5, fontWeight: 500, fill: "#1A1A2E", pointerEvents: "none" }}
                                    >
                                        {node.label} ({node.customers})
                                    </text>
                                </g>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Friction Legend */}
                <Box sx={FLOW_RIBBONS_STYLES.legend}>
                    {frictionLegend.map(item => (
                        <Box key={item.category} sx={FLOW_RIBBONS_STYLES.legendItem}>
                            <Box
                                sx={{
                                    ...(FLOW_RIBBONS_STYLES.legendDot as object),
                                    backgroundColor: item.color || FRICTION_COLORS[item.category] || "#9CA3AF"
                                }}
                            />
                            <Typography sx={FLOW_RIBBONS_STYLES.legendText}>{item.category}</Typography>
                        </Box>
                    ))}
                </Box>
            </Paper>

            {/* Customer Chips Section */}
            <Paper elevation={0} sx={FLOW_RIBBONS_STYLES.chipsSection}>
                <Box sx={FLOW_RIBBONS_STYLES.chipsHeader}>
                    <Typography sx={FLOW_RIBBONS_STYLES.chipsTitle}>
                        CUSTOMERS {selectedStage ? `· ${selectedStage.toUpperCase()}` : "· ALL STAGES"} ({displayCustomers.length})
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, background: "#F1F5F9", borderRadius: "20px", px: 1.5, py: 0.5 }}>
                        <Search16Regular style={{ color: "#94A3B8" }} />
                        <InputBase
                            placeholder="Search customer..."
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                            sx={{ fontSize: "13px", width: 150 }}
                        />
                    </Box>
                </Box>
                <Box sx={FLOW_RIBBONS_STYLES.chipsGrid}>
                    {filteredChips.length === 0 ? (
                        <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 3 }}>
                            <Typography sx={{ color: "#64748B", fontSize: "13px" }}>
                                {selectedStage
                                    ? "No customers found in this cohort."
                                    : "Click on a pipeline stage in the flow diagram above to see customers in that cohort."}
                            </Typography>
                        </Box>
                    ) : (
                        filteredChips.map(chip => (
                            <Tooltip
                                key={chip.customer}
                                title={`${chip.customer} · ${chip.deployStatus || "No status"} · ${chip.sessions} sessions`}
                                arrow
                            >
                                <Box
                                    sx={{
                                        ...(FLOW_RIBBONS_STYLES.customerChip as object),
                                        borderLeftColor: FRICTION_COLORS[chip.topFriction || ""] || DEPLOYMENT_COLORS[chip.deployStatus || ""] || "#E2E8F0",
                                        borderLeftWidth: 3,
                                        "&:hover": {
                                            background: "#E6F2FA",
                                            borderColor: "#0078D4"
                                        }
                                    }}
                                    onClick={() => onStageClick?.(chip.customer)}
                                >
                                    <Box>
                                        <Typography sx={FLOW_RIBBONS_STYLES.chipName}>{chip.customer}</Typography>
                                        <Typography sx={FLOW_RIBBONS_STYLES.chipPM}>PM · {chip.pm}</Typography>
                                    </Box>
                                </Box>
                            </Tooltip>
                        ))
                    )}
                </Box>
            </Paper>

            {/* Live Preview Usage Section */}
            <Paper elevation={0} sx={JOURNEY_BOARD_STYLES.usageSection}>
                <Box sx={JOURNEY_BOARD_STYLES.usageHeader}>
                    <Box sx={JOURNEY_BOARD_STYLES.usageTitle}>
                        <Typography sx={JOURNEY_BOARD_STYLES.usageTitleText}>LIVE PREVIEW USAGE</Typography>
                        {selectedStage && <Box sx={JOURNEY_BOARD_STYLES.filteredBadge}>FILTERED TO {selectedStage.toUpperCase()}</Box>}
                    </Box>
                    <ToggleButtonGroup
                        value={timeRange}
                        exclusive
                        onChange={handleTimeRangeChange}
                        size="small"
                    >
                        {TIME_RANGE_OPTIONS.map(opt => (
                            <ToggleButton
                                key={opt.key}
                                value={opt.key}
                                sx={{
                                    fontSize: "11px",
                                    px: 1.5,
                                    py: 0.5,
                                    textTransform: "none",
                                    "&.Mui-selected": {
                                        backgroundColor: THEME.primary,
                                        color: "#FFFFFF",
                                        "&:hover": { backgroundColor: THEME.primaryDark }
                                    }
                                }}
                            >
                                {opt.text}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
                <Typography sx={JOURNEY_BOARD_STYLES.usageSubtitle}>
                    {selectedStage ? `Selection: ${selectedStage}` : "All customers"} · {livePreviewUsage?.customerCount || displayCustomers.length} of {customerChips.length} customers with telemetry · {timeRange.toLowerCase()} · as of {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </Typography>
                <Box sx={JOURNEY_BOARD_STYLES.usageTiles}>
                    <Box sx={JOURNEY_BOARD_STYLES.usageTile}>
                        <Typography sx={JOURNEY_BOARD_STYLES.usageTileValue}>
                            {livePreviewUsage?.uniqueUsers || "—"}
                        </Typography>
                        <Typography sx={JOURNEY_BOARD_STYLES.usageTileLabel}>UNIQUE USERS</Typography>
                    </Box>
                    <Box sx={JOURNEY_BOARD_STYLES.usageTile}>
                        <Typography sx={JOURNEY_BOARD_STYLES.usageTileValue}>
                            {livePreviewUsage?.uniqueSessions || "—"}
                        </Typography>
                        <Typography sx={JOURNEY_BOARD_STYLES.usageTileLabel}>UNIQUE SESSIONS</Typography>
                    </Box>
                    <Box sx={JOURNEY_BOARD_STYLES.usageTile}>
                        <Typography sx={JOURNEY_BOARD_STYLES.usageTileValue}>
                            {livePreviewUsage?.totalSessions || "—"}
                        </Typography>
                        <Typography sx={JOURNEY_BOARD_STYLES.usageTileLabel}>TOTAL SESSIONS</Typography>
                    </Box>
                    <Box sx={JOURNEY_BOARD_STYLES.usageTile}>
                        <Typography sx={JOURNEY_BOARD_STYLES.usageTileValue}>
                            {livePreviewUsage?.pageViews?.toLocaleString() || "—"}
                        </Typography>
                        <Typography sx={JOURNEY_BOARD_STYLES.usageTileLabel}>PAGE VIEWS</Typography>
                    </Box>
                    <Box sx={JOURNEY_BOARD_STYLES.usageTile}>
                        <Typography sx={JOURNEY_BOARD_STYLES.usageTileValue}>
                            {livePreviewUsage?.playbookRuns || "—"}
                        </Typography>
                        <Typography sx={JOURNEY_BOARD_STYLES.usageTileLabel}>PLAYBOOK RUNS</Typography>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default FlowRibbonsSection;
